import { Component, computed, input, InputSignal, linkedSignal, OutputEmitterRef, signal, WritableSignal } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import type { FontWeight, CssColor } from '@appTypes';

@Component({
  selector: 'app-abstract-control',
  template: '',
  standalone: true,
  imports: [],
})
/* Componente base para los controles reactivos */
export abstract class AbstractControl<T, K> implements ControlValueAccessor {
  // Inputs
  abstract $value: InputSignal<T>;
  $disabled = input<boolean | undefined>(undefined, { alias: 'isDisabled' });
  $readonly = input<boolean | undefined>(undefined, { alias: 'isReadonly' });
  $label = input<string | null>(null, { alias: 'label' });
  $labelFontSize = input<string>('--font-size-medium', { alias: 'labelFontSize' });
  $inputFontSize = input<string>('--font-size-medium', { alias: 'inputFontSize' });
  $labelWeight = input<FontWeight>('normal', { alias: 'labelWeight' });
  $labelColor = input<CssColor>('--black-white', { alias: 'labelColor' });
  $labelWrap = input<boolean>(false, { alias: 'labelWrap' });
  $innerPadding = input<string | undefined>(undefined, { alias: 'innerPadding' });
  $_gap = input<string | undefined>(undefined, { alias: 'gap' });
  $gap = computed<string>(() => {
    const gap = this.$_gap();
    const controlPosition = this.$controlPosition();
    return gap !== undefined ? gap : (controlPosition !== 'top' && controlPosition !== 'bottom' ? '--gap-semi' : '--gap-small');
  });
  $controlPosition = input<'default' | 'opposite' | 'top' | 'bottom'>('default', { alias: 'controlPosition' });
  $verticalAlignment = input<'start' | 'center' | 'end'>('start', { alias: 'verticalAlignment' });
  $tooltip = input<string>('', { alias: 'tooltip' });
  $tooltipWhenDisabled = input<string>('', { alias: 'tooltipWhenDisabled' });
  $emitAtStart = input<boolean>(false, { alias: 'emitAtStart' });

  // Outputs
  abstract valueChange: OutputEmitterRef<K>;

  // Signals / atributos
  abstract $controlValue: WritableSignal<T>;
  protected $controlDisabled = signal<boolean>(false);

  // Callbacks que registra Angular
  protected onChange: (val: T) => void = () => {};
  protected onTouched: () => void = () => {};

  // Métodos de la interfaz controlValueAccessor
  writeValue(val: T): void {
    this.$controlValue.set(val);
  }
  registerOnChange(fn: (val: T) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }

  // Maneja el evento de actualización de valor
  updateValue(newValue: T, triggerOnTouched: boolean = true): void {
    this.$controlValue.set(newValue);
    this.writeValue(newValue);
    this.onChange(newValue);
    if (triggerOnTouched)
      this.onTouched();
  }

  // Maneja el evento de actualización de estado de activado / desactivado
  setDisabledState(isDisabled: boolean): void {
    this.$controlDisabled.set(isDisabled);
  }

  // Maneja el evento de cambio de valor desde el input
  abstract onValueChange(event: Event): void;
}
